import { ref, reactive } from "vue";

declare var currentLanguage: string;

class LanguageManager {
    private defaultLang = "pl";
    private language = ref(this.defaultLang);
    private translations = reactive<Record<string, string>>({});
    private static instance: LanguageManager | null = null;
    private commonLangPath = "/langs/common/"; // Ścieżka do tłumaczeń w common
    private appLangPath = "/langs/"; // Ścieżka do tłumaczeń w aktualnej aplikacji (frontend lub backend - dostosuj, jeśli trzeba)

    private constructor() {
        const storedLang = localStorage.getItem("language");
        this.language.value = storedLang ? storedLang : this.defaultLang;
        this.loadLanguageFiles(this.language.value);
    }

    public static getInstance(): LanguageManager {
        if (!LanguageManager.instance) {
            LanguageManager.instance = new LanguageManager();
        }
        return LanguageManager.instance;
    }

    private loadLanguageFiles(lang: string): void {
        this.loadLanguageFile(this.commonLangPath + `${lang}.json`);
        this.loadLanguageFile(this.appLangPath + `${lang}.json`);
    }

    private loadLanguageFile(path: string): void {
        const request = new XMLHttpRequest();
        request.open("GET", path, true);
        request.onreadystatechange = () => {
            if (request.readyState === 4) {
                if (request.status === 200) {
                    try {
                        Object.assign(this.translations, JSON.parse(request.responseText));
                        this.updateDomTranslations();
                    } catch (error) {
                        console.error(`Error parsing language file: ${path}`, error);
                    }
                } else if (request.status !== 404) {
                    console.error(`Failed to load language file: ${path}, status: ${request.status}`);
                }
                // Ignoruj błąd 404 - plik może nie istnieć w jednej z lokalizacji
            }
        };
        request.send();
    }

    private updateDomTranslations(): void {
        for (const key in this.translations) {
            const elements = document.querySelectorAll(`[data-key="${key}"]`);
            elements.forEach((elem) => {
                if (elem instanceof HTMLInputElement && elem.type === "checkbox") {
                    const labelElement = elem.closest("label");

                    if (labelElement) {
                        labelElement.childNodes.forEach((node) => {
                            if (node.nodeType === Node.TEXT_NODE && !node.textContent?.includes("<input")) {
                                node.textContent = " " + this.translations[key];
                            }
                        });
                    }
                } else {
                    elem.textContent = this.translations[key];
                }
            });
            const placeholders = document.querySelectorAll(`[data-placeholder="${key}"]`);
            placeholders.forEach((elem) => {
                (elem as HTMLInputElement).placeholder = this.translations[key];
            });
        }
    }

    /**
     * Helper do tłumaczeń z obsługą liczby mnogiej
     */
    private pluralTranslation(key: string, n: number): string {
        // Specjalny przypadek dla "=0" z Yii2
        if (n === 0) {
            const zeroKey = `${key}_0`;
            const zeroTranslation = this.getTranslation(zeroKey);
            if (zeroTranslation !== zeroKey) {
                return zeroTranslation.replace("{n}", "0");
            }
        }

        const lang = this.getCurrentLanguage();
        const pr = new Intl.PluralRules(lang);
        const rule = pr.select(n); // Zwróci: 'one', 'two', 'few', 'many' lub 'other'

        // Szukamy klucza np. "time.minutes_ago_few", jak nie ma to "time.minutes_ago_other", a ostatecznie samo "time.minutes_ago"
        const ruleKey = `${key}_${rule}`;
        const otherKey = `${key}_other`;

        let translation = this.getTranslation(ruleKey);

        if (translation === ruleKey) {
            translation = this.getTranslation(otherKey);
            if (translation === otherKey) {
                translation = this.getTranslation(key);
            }
        }

        return translation.replace("{n}", n.toString());
    }

    private setLanguage(lang: string): void {
        localStorage.setItem("language", lang);
        this.language.value = lang;
        this.loadLanguageFiles(lang);
    }

    public getTranslation(key: string): string {
        return this.translations[key] || key;
    }

    public getPluralTranslation(key: string, n?: number): string {
        if (n === undefined) {
            return this.getTranslation(key);
        }
        return this.pluralTranslation(key, n);
    }

    public getCurrentLanguage(): string {
        return this.language.value;
    }

    private initLanguage(): void {
        this.setLanguage(this.language.value);

        const languageElements = document.getElementsByClassName("language");

        Array.from(languageElements).forEach((dropdown) => {
            dropdown.addEventListener("click", (event) => {
                const target = event.currentTarget as HTMLElement;
                const lang = target.getAttribute("data-lang");
                if (lang) {
                    this.setLanguage(lang);
                }
            });
        });
    }

    public initializeEventListeners(): void {
        if (currentLanguage && currentLanguage !== "") {
            this.language = ref(currentLanguage);
        }
        this.initLanguage();
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const languageManagerInstance = LanguageManager.getInstance();
    languageManagerInstance.initializeEventListeners();
});

const languageManager = LanguageManager.getInstance();
export default languageManager;
